import { useNavigate } from "react-router-dom";
import { Search, Settings, Trash2 } from "lucide-react";
import { AppShell } from "@/components/layout/app-shell";
import { NotificationBell } from "@/components/layout/notification-bell";
import { UserMenu } from "@/components/layout/user-menu";
import type { RailNavItem } from "@/components/layout/sidebar-rail";
import { SearchOverlay } from "@/components/layout/search-overlay";
import {
  SearchOverlayProvider,
  useSearchOverlay,
} from "@/components/layout/search-overlay-context";
import {
  HelpWidgetProvider,
  useHelpWidget,
} from "@/components/layout/help-widget-context";
import { HelpWidget } from "@/components/layout/help-widget";
import { useSearchTrigger } from "@/components/layout/use-search-trigger";
import { useAuth, useLogout } from "@/hooks/use-auth";
import { roleDisplayName } from "@/lib/api-types";

export default function AppLayout() {
  // Provider sits above AppShell so both the rail (Search icon) AND deeper
  // pages (project-detail / folder-detail search buttons) share the same
  // overlay state.
  return (
    <SearchOverlayProvider>
      <HelpWidgetProvider>
        <AppLayoutInner />
        <AppOverlayHost />
        <HelpWidget />
      </HelpWidgetProvider>
    </SearchOverlayProvider>
  );
}

function AppLayoutInner() {
  const { user, role, workspaceName } = useAuth();
  const logout = useLogout();
  const navigate = useNavigate();

  // Rail Search icon: opens overlay on desktop, navigates to /search on mobile.
  const { openWidget } = useHelpWidget();
  const triggerSearch = useSearchTrigger("/app/search");

  // Kept identical to the admin/full-access rail (admin-layout.tsx) so every
  // workspace role sees the same primary nav: Search · Trash · Notifications ·
  // Settings. Home (brand) already routes to /app/projects, so a separate
  // Projects icon is redundant; uploads happen inside a project via the "+".
  const NAV: RailNavItem[] = [
    { kind: "action", label: "Search", icon: Search, onClick: triggerSearch },
    { kind: "link", to: "/app/trash", label: "Trash", icon: Trash2 },
    {
      kind: "custom",
      key: "notifications",
      render: () => <NotificationBell to="/app/notifications" />,
    },
    { kind: "link", to: "/app/settings", label: "Settings", icon: Settings },
  ];

  return (
    <AppShell
      onOpenHelp={() => openWidget("help")}
      onOpenFeatureRequest={() => openWidget("message", "Feature request")}
      brandTo="/app/projects"
      primaryNav={NAV}
      searchPlaceholder="Search projects…"
      projectsBasePath="/app/projects"
      userMenu={
        <UserMenu
          name={user?.name || user?.email || "Account"}
          email={user?.email}
          roleLabel={roleDisplayName(role)}
          workspaceName={workspaceName ?? undefined}
          settingsTo="/app/settings"
          isLoggingOut={logout.isPending}
          onLogout={() =>
            logout.mutate(undefined, { onSuccess: () => navigate("/login", { replace: true }) })
          }
        />
      }
    />
  );
}

function AppOverlayHost() {
  const { open, scope, close } = useSearchOverlay();
  return <SearchOverlay open={open} onClose={close} scope={scope} basePath="/app" />;
}
